import { useDecision } from '@optimizely/react-sdk'
import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  ModerationEntityType,
  ModerationStatus,
  SpaceJoinRequestStatus,
} from '@tribeplatform/gql-client/types'
import {
  useAuthToken,
  useModerationItems,
  useSpace,
  useSpaceMembershipRequests,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'

import { FeaturesList } from '../../lib/optimizely/feature.list'

export const PendingModerationAlert = ({ spaceId = undefined }) => {
  const [decisionModeration] = useDecision(FeaturesList.NewModerationPanel)

  const {
    data: { network },
  } = useAuthToken()

  const { data: space } = useSpace({
    variables: { id: spaceId },
    useQueryOptions: {
      enabled: !!spaceId && decisionModeration.enabled,
    },
  })

  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])
  const [canApproveSpaceMembershipRequest] = hasScopesPermission(space, [
    'approveSpaceMembershipRequest',
  ])

  const isModerator = canApproveSpaceMembershipRequest || canUpdateNetwork

  const { data: moderationItemsData, isLoading: moderationItemsLoading } =
    useModerationItems({
      fields: {
        entity: {
          onPost: 'basic',
        },
      },
      variables: {
        limit: 0,
        spaceId,
        entityType: ModerationEntityType.POST,
        status: ModerationStatus.REVIEW,
      },
      useInfiniteQueryOptions: {
        enabled: isModerator,
      },
    })

  const { data: memberRequestsData, isLoading: memberRequestsLoading } =
    useSpaceMembershipRequests({
      variables: {
        spaceId,
        status: SpaceJoinRequestStatus.PENDING,
      },
      fields: {},
      useQueryOptions: {
        enabled: isModerator && !!spaceId,
      },
    })

  const totalModeratonItems = moderationItemsData?.pages?.[0]?.totalCount || 0
  const totalMemberRequests = memberRequestsData?.length || 0
  const totalModerationItems = totalModeratonItems + totalMemberRequests

  if (
    !isModerator ||
    !decisionModeration.enabled ||
    typeof window === 'undefined' ||
    !totalModerationItems ||
    moderationItemsLoading ||
    memberRequestsLoading
  )
    return null

  return (
    <Alert
      className="mb-5 shadow"
      title="There are items pending moderation."
      status="warning"
    >
      <div className="space-x-4">
        {totalModeratonItems > 0 && (
          <Link
            to={
              space?.id
                ? `/settings/moderation/${space?.id}/pending-posts`
                : '/settings/moderation/pending-posts'
            }
          >
            View {totalModeratonItems} pending{' '}
            {totalModeratonItems === 1 ? 'post' : 'posts'}
          </Link>
        )}
        {totalMemberRequests > 0 && (
          <Link to={`/settings/moderation/${space?.id}/member-requests`}>
            View {totalMemberRequests}{' '}
            {totalMemberRequests === 1 ? 'member request' : 'member requests'}
          </Link>
        )}
      </div>
    </Alert>
  )
}
